<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import debounce from 'lodash.debounce'
import { cn } from '@/lib/utils'

const props = withDefaults(defineProps<{
  modelValue: string
  label?: string
  placeholder?: string
  loading?: boolean
  disabled?: boolean
}>(), {
  label: 'Search',
  placeholder: 'Search...',
  loading: false,
  disabled: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'search': [value: string]
  'clear': []
}>()

const inputValue = computed({
  get: () => props.modelValue,
  set: (v: string) => emit('update:modelValue', v),
})

const debouncedSearch = debounce((v: string) => {
  emit('search', v)
}, 300)

watch(inputValue, (newVal) => {
  debouncedSearch(newVal)
})

function handleClear() {
  emit('update:modelValue', '')
  emit('clear')
}
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <label
      v-if="props.label"
      class="text-sm font-medium text-[color:var(--color-ink)]"
    >
      {{ props.label }}
    </label>
    <div class="relative flex items-center">
      <input
        :type="'text'"
        :value="inputValue"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        @input="(e) => emit('update:modelValue', (e.target as HTMLInputElement).value)"
        :class="cn(
          'flex h-10 w-full px-3 py-2 pr-10 text-sm transition-colors',
          'rounded-[var(--radius-md)]',
          'bg-[color:var(--color-surface)] text-[color:var(--color-ink)]',
          'placeholder:text-[color:var(--color-ink-soft)] placeholder:opacity-60',
          'focus:outline-none focus:ring-2 focus:ring-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'border border-[color:var(--color-border-soft)]',
          'focus:border-[color:var(--color-border-strong)] focus:ring-primary/50'
        )"
        :aria-label="props.label"
      />
      <!-- Loading spinner -->
      <span
        v-if="props.loading"
        class="absolute right-3 text-[color:var(--color-ink-soft)] animate-spin"
        aria-hidden="true"
      >
        &#8635;
      </span>
      <!-- Clear button -->
      <button
        v-else-if="inputValue.length > 0"
        type="button"
        class="absolute right-3 text-[color:var(--color-ink-soft)] hover:text-[color:var(--color-ink)] transition-colors"
        aria-label="Clear search"
        @click="handleClear"
      >
        &#10005;
      </button>
    </div>
  </div>
</template>
